import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Raffle, RaffleTask } from "@/campaigns/modules/raffle/types";
import { isTaskMaxed, rafflePhase } from "@/campaigns/modules/raffle/types";
import { TurnoverContributionNote } from "@/components/campaigns/campaign-contribution-rates";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { TicketBadge, TicketPill } from "./ticket-badge";

const ROW =
  "relative flex w-full items-center justify-between gap-3 py-3 first:pt-1 last:pb-1 [&:not(:first-child)]:border-t [&:not(:first-child)]:border-foreground/[0.06]";
const CAPTION =
  "font-body text-[12px] leading-4 text-muted-foreground tabular-nums lg:text-[13px] lg:leading-[18px]";

/** What a completed round pays, as the chip under a task that is not being worked on: "+1 per
 *  login", "+1 per €50". `threshold` arrives rendered (a count for a login task, display money
 *  otherwise). */
const perLabel = (
  task: RaffleTask,
  t: ReturnType<typeof useTranslation>["t"]
) =>
  task.kind === "login"
    ? t("raffle.ticketsPerLogin", "+{{tickets}} per login", {
        tickets: task.tickets_per_completion,
        count: Number(task.threshold) || 1,
      })
    : t("raffle.ticketsPerAmount", "+{{tickets}} per {{amount}}", {
        tickets: task.tickets_per_completion,
        amount: task.threshold,
      });

/** The far end of an open row: all collected, or what a completed round pays. */
const TaskStatus = ({ task }: { task: RaffleTask }) => {
  const { t } = useTranslation();
  if (isTaskMaxed(task)) {
    return (
      <span className="flex shrink-0 items-center gap-1.5 font-heading text-[13px] leading-[18px] font-semibold text-foreground-secondary lg:text-[14px]">
        <Check className="size-4" strokeWidth={3} aria-hidden />
        {t("raffle.allCollected", "All tickets collected")}
      </span>
    );
  }
  return (
    <TicketPill>
      {t("raffle.ticketPill", "+{{count}} ticket", {
        count: task.tickets_per_completion,
      })}
    </TicketPill>
  );
};

/** A task the player is earning on right now: the tickets it has paid on a stub, then the title
 *  and its cap, then what a completed round pays. */
const OpenTaskRow = ({ task }: { task: RaffleTask }) => {
  const maxed = isTaskMaxed(task);
  const { t } = useTranslation();
  return (
    <>
      <div className={ROW}>
        <span className="flex min-w-0 items-center gap-3">
          <TicketBadge count={task.tickets} muted={maxed} />
          <span className="flex min-w-0 flex-col">
            <span
              className={cn(
                "font-heading text-[15px] leading-5 font-semibold text-foreground lg:text-[16px]",
                maxed && "text-foreground-secondary"
              )}
            >
              {task.title}
            </span>
            {task.max_tickets > 0 && (
              <span className={CAPTION}>
                {t("raffle.taskCap", "Max. {{max}}", { max: task.max_tickets })}
              </span>
            )}
          </span>
        </span>
        <TaskStatus task={task} />
      </div>
      {/* Contribution rates only ever apply to turnover, the one kind where a round can fill
          slower than the wagers behind it. */}
      {!maxed && task.kind === "turnover" && <TurnoverContributionNote />}
    </>
  );
};

/** A task not being earned on — the raffle is closed, or the player has not joined: what it pays
 *  and, once there is something to show, what it paid. */
const QuietTaskRow = ({
  task,
  joined,
  closed,
}: {
  task: RaffleTask;
  joined: boolean;
  closed: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className={ROW}>
      <span className="flex min-w-0 flex-col gap-1.5">
        <span className="font-heading text-[15px] leading-5 font-semibold text-foreground-secondary lg:text-[17px] lg:leading-6">
          {task.title}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          <Badge variant="ghost" color="neutral">
            {perLabel(task, t)}
          </Badge>
          {joined && task.progress !== null && (
            <span className={CAPTION}>
              {task.max_tickets > 0
                ? t("raffle.earnedOf", "{{tickets}} / {{max}} earned", {
                    tickets: task.tickets,
                    max: task.max_tickets,
                  })
                : t("raffle.earnedCount", "{{tickets}} earned", {
                    tickets: task.tickets,
                  })}
            </span>
          )}
        </span>
      </span>
      {closed && (
        <Badge variant="ghost" color="locked" className="shrink-0">
          {t("raffle.closed", "Closed")}
        </Badge>
      )}
    </div>
  );
};

/**
 * "Earn tickets": every task with what it pays. While the raffle is open a joined player sees
 * their stub per task and the round in play; before joining, and once the raffle has closed, the
 * rows are the quiet list — what each task pays and what it paid. Task types are only login,
 * deposit and turnover.
 */
export const RaffleTasksCard = ({
  raffle,
  joined,
  className,
}: {
  raffle: Raffle;
  joined: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const live = rafflePhase(raffle) === "live";
  if (raffle.tasks.length === 0) return null;

  return (
    <Card
      title={t("raffle.earnTickets", "Earn tickets")}
      className={cn("flex flex-col", className)}
      titleRowClassName="mb-0"
    >
      {raffle.tasks.map((task) =>
        live && joined ? (
          <OpenTaskRow key={task.id} task={task} />
        ) : (
          <QuietTaskRow
            key={task.id}
            task={task}
            joined={joined}
            closed={!live}
          />
        )
      )}
    </Card>
  );
};
